import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, NgZone } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { readSseStream } from "./sse-reader";
import {
  GuestOwnerBootstrapRequest,
  GuestOwnerBootstrapResponse,
  GuestOwnerCurrentEventResponse,
  GuestOwnerEventRequest,
  GuestOwnerEventResponse,
  GuestOwnerInviteResponse,
  GuestOwnerParticipantCreateRequest,
  GuestOwnerParticipantDto,
  GuestOwnerParticipantUpdateRequest,
  GuestOwnerProfileUpdateRequest,
  PublicEventInviteChatMessageResponse,
  PublicEventInviteParticipantResponse,
  PublicEventInvitePreviewResponse,
  PublicEventAuthenticatedJoinResponse,
  PublicEventRsvpRequest,
  PublicEventRsvpResponse
} from "../models/guestOwner";
import { GuestOwnerSessionService } from "./guest-owner-session.service";

export interface GuestOwnerParticipantsStreamEvent {
  type: "connected" | "participants.changed" | "heartbeat" | string;
  data: any;
}

export interface PublicEventInviteStreamEvent {
  type: "connected" | "heartbeat" | "participants.changed" | "event.updated" | "event.deleted" | string;
  data: any;
}

@Injectable()
export class GuestOwnerService {
  private apiUrl = environment.API_BASE_URL + "/guest-owner";
  private publicInviteApiUrl = environment.API_BASE_URL + "/public-event-invite";

  constructor(
    private http: HttpClient,
    private sessionService: GuestOwnerSessionService,
    private zone: NgZone
  ) {}

  public bootstrap(name?: string): Observable<GuestOwnerBootstrapResponse> {
    const request: GuestOwnerBootstrapRequest = {
      clientType: "WEB"
    };
    const managerName = (name || "").trim();
    if (managerName) {
      request.name = managerName;
      request.managerName = managerName;
    }

    return this.http.post<GuestOwnerBootstrapResponse>(`${this.apiUrl}/bootstrap`, JSON.stringify(request), this.jsonOptions());
  }

  public updateMe(request: GuestOwnerProfileUpdateRequest): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/me`, JSON.stringify(request), this.guestOwnerOptions());
  }

  public events(): Observable<GuestOwnerEventResponse[]> {
    return this.http.get<GuestOwnerEventResponse[]>(`${this.apiUrl}/events`, this.guestOwnerOptions());
  }

  public currentEvent(): Observable<GuestOwnerCurrentEventResponse> {
    return this.http.get<GuestOwnerCurrentEventResponse>(`${this.apiUrl}/current-event`, this.guestOwnerOptions());
  }

  public createEvent(request: GuestOwnerEventRequest): Observable<GuestOwnerEventResponse> {
    return this.http.post<GuestOwnerEventResponse>(`${this.apiUrl}/events`, JSON.stringify(request), this.guestOwnerOptions());
  }

  public updateEvent(eventId: string, request: GuestOwnerEventRequest): Observable<GuestOwnerEventResponse> {
    return this.http.put<GuestOwnerEventResponse>(`${this.apiUrl}/events/${eventId}`, JSON.stringify(request), this.guestOwnerOptions());
  }

  public invite(eventId: string): Observable<GuestOwnerInviteResponse> {
    return this.http.post<GuestOwnerInviteResponse>(`${this.apiUrl}/events/${eventId}/invite`, null, this.guestOwnerOptions());
  }

  public deleteEvent(eventId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/events/${eventId}`, this.guestOwnerOptions());
  }

  public participants(eventId: string): Observable<GuestOwnerParticipantDto[]> {
    return this.http.get<GuestOwnerParticipantDto[]>(`${this.apiUrl}/events/${eventId}/participants`, this.guestOwnerOptions());
  }

  public addParticipant(
    eventId: string,
    request: GuestOwnerParticipantCreateRequest
  ): Observable<GuestOwnerParticipantDto> {
    return this.http.post<GuestOwnerParticipantDto>(
      `${this.apiUrl}/events/${eventId}/participants`,
      JSON.stringify(request),
      this.guestOwnerOptions()
    );
  }

  public updateParticipant(
    eventId: string,
    participantId: string,
    request: GuestOwnerParticipantUpdateRequest
  ): Observable<GuestOwnerParticipantDto> {
    return this.http.put<GuestOwnerParticipantDto>(
      `${this.apiUrl}/events/${eventId}/participants/${participantId}`,
      JSON.stringify(request),
      this.guestOwnerOptions()
    );
  }

  public deleteParticipant(eventId: string, participantId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/events/${eventId}/participants/${participantId}`, this.guestOwnerOptions());
  }

  public openParticipantsStream(
    eventId: string,
    onEvent: (event: GuestOwnerParticipantsStreamEvent) => void,
    onError: (error: any) => void
  ): () => void {
    const session = this.sessionService.getSession();
    const controller = new AbortController();
    let closed = false;

    fetch(`${this.apiUrl}/events/${eventId}/participants/stream`, {
      headers: {
        "Accept": "text/event-stream",
        "X-Guest-Owner-Token": session ? session.guestOwnerToken : ""
      },
      signal: controller.signal
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Participants stream failed: ${response.status}`);
      }
      if (!response.body) {
        throw new Error("Participants stream response body is not readable.");
      }
      return this.readParticipantsStream(response.body, onEvent, () => closed);
    }).then(() => {
      if (!closed) this.notifyInAngularZone(() => onError(new Error("Participants stream ended.")));
    }).catch((error) => {
      if (!closed) {
        this.notifyInAngularZone(() => onError(error));
      }
    });

    return () => {
      closed = true;
      controller.abort();
    };
  }

  public previewInvite(inviteToken: string): Observable<PublicEventInvitePreviewResponse> {
    return this.http.get<PublicEventInvitePreviewResponse>(`${this.publicInviteApiUrl}/${inviteToken}`, this.jsonOptions());
  }

  public publicInviteParticipants(inviteToken: string): Observable<PublicEventInviteParticipantResponse[]> {
    return this.http.get<PublicEventInviteParticipantResponse[]>(
      `${this.publicInviteApiUrl}/${inviteToken}/participants`,
      this.jsonOptions()
    );
  }

  public publicInviteChatMessages(inviteToken: string): Observable<PublicEventInviteChatMessageResponse[]> {
    return this.http.get<PublicEventInviteChatMessageResponse[]>(
      `${this.publicInviteApiUrl}/${inviteToken}/chat/messages`,
      this.jsonOptions()
    );
  }

  public guestOwnerChatMessages(eventId: string): Observable<PublicEventInviteChatMessageResponse[]> {
    return this.http.get<PublicEventInviteChatMessageResponse[]>(
      `${this.apiUrl}/events/${eventId}/chat/messages`,
      this.guestOwnerOptions()
    );
  }

  public openPublicInviteStream(
    inviteToken: string,
    onEvent: (event: PublicEventInviteStreamEvent) => void,
    onError: (error: any) => void
  ): () => void {
    const eventSource = new EventSource(`${this.publicInviteApiUrl}/${encodeURIComponent(inviteToken)}/stream`);
    let closed = false;

    const connectedListener = this.publicInviteStreamListener("connected", onEvent);
    const heartbeatListener = this.publicInviteStreamListener("heartbeat", onEvent);
    const participantsChangedListener = this.publicInviteStreamListener("participants.changed", onEvent);
    const eventUpdatedListener = this.publicInviteStreamListener("event.updated", onEvent);
    const eventDeletedListener = this.publicInviteStreamListener("event.deleted", onEvent);
    const errorListener = (error: any) => {
      if (!closed) {
        this.notifyInAngularZone(() => onError(error));
      }
    };

    eventSource.addEventListener("connected", connectedListener);
    eventSource.addEventListener("heartbeat", heartbeatListener);
    eventSource.addEventListener("participants.changed", participantsChangedListener);
    eventSource.addEventListener("event.updated", eventUpdatedListener);
    eventSource.addEventListener("event.deleted", eventDeletedListener);
    eventSource.addEventListener("error", errorListener);

    return () => {
      closed = true;
      eventSource.removeEventListener("connected", connectedListener);
      eventSource.removeEventListener("heartbeat", heartbeatListener);
      eventSource.removeEventListener("participants.changed", participantsChangedListener);
      eventSource.removeEventListener("event.updated", eventUpdatedListener);
      eventSource.removeEventListener("event.deleted", eventDeletedListener);
      eventSource.removeEventListener("error", errorListener);
      eventSource.close();
    };
  }

  public rsvp(inviteToken: string, request: PublicEventRsvpRequest): Observable<PublicEventRsvpResponse> {
    return this.http.post<PublicEventRsvpResponse>(
      `${this.publicInviteApiUrl}/${inviteToken}/rsvp`,
      JSON.stringify(request),
      this.jsonOptions()
    );
  }

  public joinAuthenticatedInvite(inviteToken: string): Observable<PublicEventAuthenticatedJoinResponse> {
    return this.http.post<PublicEventAuthenticatedJoinResponse>(
      `${this.publicInviteApiUrl}/${encodeURIComponent(inviteToken)}/me/join`,
      {},
      this.jsonOptions()
    );
  }

  public authenticatedInviteMembership(inviteToken: string): Observable<PublicEventAuthenticatedJoinResponse> {
    return this.http.get<PublicEventAuthenticatedJoinResponse>(
      `${this.publicInviteApiUrl}/${encodeURIComponent(inviteToken)}/me`, this.jsonOptions()
    );
  }

  public publicInviteUrl(invite: GuestOwnerInviteResponse): string {
    if (!invite) {
      return "";
    }

    if (invite.invitePath && invite.invitePath.indexOf("http") === 0) {
      return invite.invitePath;
    }

    const path = invite.invitePath || `/join/${encodeURIComponent(invite.inviteToken)}`;
    const base = ["https://app.staging.footballer.zone", "https://next.footballer.zone"].includes(window.location.origin)
      ? window.location.origin : `${window.location.origin}/#`;
    return `${base}${path.charAt(0) === "/" ? path : "/" + path}`;
  }

  private guestOwnerOptions() {
    const session = this.sessionService.getSession();
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
        "X-Guest-Owner-Token": session ? session.guestOwnerToken : ""
      })
    };
  }

  private jsonOptions() {
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json"
      })
    };
  }

  private async readParticipantsStream(
    body: ReadableStream<Uint8Array>,
    onEvent: (event: GuestOwnerParticipantsStreamEvent) => void,
    isClosed: () => boolean
  ): Promise<void> {
    return readSseStream(body, event => this.notifyInAngularZone(() => onEvent(event)), isClosed);
  }

  private publicInviteStreamListener(
    type: string,
    onEvent: (event: PublicEventInviteStreamEvent) => void
  ): EventListener {
    return ((messageEvent: MessageEvent) => {
      this.notifyInAngularZone(() => onEvent({
        type,
        data: this.parseStreamData(messageEvent.data)
      }));
    }) as EventListener;
  }

  private notifyInAngularZone(callback: () => void): void {
    if (NgZone.isInAngularZone()) {
      callback();
      return;
    }

    this.zone.run(callback);
  }

  private parseStreamData(dataText: string): any {
    if (!dataText) {
      return null;
    }

    try {
      return JSON.parse(dataText);
    } catch (error) {
      return dataText;
    }
  }
}
